import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Lock, KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function AdminRecovery() {
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
        if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
        setLoading(true);
        try {
            const res = await axios.post(`${BASE}/api/v1/auth/admin/recover/`, {
                recovery_code: code.trim().toUpperCase(),
                new_password: password,
            });
            setSuccess(res.data.username);
        } catch (err) {
            setError(err.response?.data?.error || 'Error al procesar la recuperación.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pantalla-centrada">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <button
                    onClick={() => navigate('/admin/login')}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
                >
                    <ArrowLeft size={15} /> Volver al login
                </button>

                <div className="bg-white rounded-3xl shadow-xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-alerta-100 p-3 rounded-2xl">
                            <ShieldAlert size={24} className="text-alerta-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-900 tracking-tight">Recuperar Acceso</h1>
                            <p className="text-xs text-slate-500 font-medium">Panel de Administración</p>
                        </div>
                    </div>

                    {success ? (
                        <div className="text-center space-y-4">
                            <div className="bg-exito-50 border border-exito-200 rounded-2xl p-6">
                                <CheckCircle size={32} className="text-exito-500 mx-auto mb-3" />
                                <p className="font-black text-exito-800">¡Contraseña restablecida!</p>
                                <p className="text-sm text-exito-700 mt-1">
                                    Usuario: <strong>{success}</strong>
                                </p>
                            </div>
                            <p className="text-xs text-alerta-700 bg-alerta-50 border border-alerta-200 rounded-xl p-3 font-medium">
                                El código de recuperación fue invalidado. Generá uno nuevo desde Configuración Global una vez que ingreses.
                            </p>
                            <button
                                onClick={() => navigate('/admin/login')}
                                className="w-full bg-slate-900 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs hover:bg-slate-700 transition-colors"
                            >
                                Ir al Login
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-slate-500 font-medium">
                                Ingresá el código de recuperación de emergencia que generaste desde el panel de configuración.
                                El código tiene formato <span className="font-mono font-black text-slate-700">XXXXXX-XXXXXX-XXXXXX-XXXXXX</span>.
                            </div>

                            {error && (
                                <div className="bg-error-50 text-error-600 text-sm font-bold p-3 rounded-xl border border-error-100">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label htmlFor="rescate-codigo" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">
                                    Código de Recuperación
                                </label>
                                <div className="relative">
                                    <KeyRound size={16} className="icono-campo" />
                                    <input id="rescate-codigo"
                                        required
                                        type="text"
                                        placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX"
                                        className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 font-mono font-bold text-sm focus:ring-2 focus:ring-alerta-400/50 outline-none tracking-widest uppercase"
                                        value={code}
                                        onChange={e => setCode(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="rescate-clave" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">
                                    Nueva Contraseña
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="icono-campo" />
                                    <input id="rescate-clave"
                                        required
                                        type="password"
                                        placeholder="Mínimo 8 caracteres"
                                        className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 font-bold text-sm focus:ring-2 focus:ring-alerta-400/50 outline-none"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="rescate-clave-confirmada" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">
                                    Confirmar Contraseña
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="icono-campo" />
                                    <input id="rescate-clave-confirmada"
                                        required
                                        type="password"
                                        placeholder="Repetí la contraseña"
                                        className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 font-bold text-sm focus:ring-2 focus:ring-alerta-400/50 outline-none"
                                        value={confirm}
                                        onChange={e => setConfirm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-alerta-600 hover:bg-alerta-700 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50 transition-colors shadow-lg"
                            >
                                {loading ? 'Verificando...' : 'Restablecer Contraseña'}
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
